import React, { useState, useContext, useEffect } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import { errorToast } from '../../lib/helpers';
import clsx from 'clsx';
import { MdCarRepair } from 'react-icons/md';
import { WrenchScrewdriverIcon, BoltIcon } from '@heroicons/react/24/solid';
import { GiOilBarrel, GiOilDrum } from 'react-icons/gi';
import { ClockIcon } from '@heroicons/react/24/outline';
import MessageDialog from '../MessageDialog';

const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const ServiceIcons = ({ mot, oil, ev }) => (
  <div className="flex space-x-1">
    <MdCarRepair className={`w-4 h-4 ${mot ? 'text-green-600' : 'text-gray-400'}`} title="MOT" />
    <WrenchScrewdriverIcon className={`w-4 h-4 ${oil ? 'text-green-600' : 'text-gray-400'}`} title="Oil Service" />
    <BoltIcon className={`w-4 h-4 ${ev ? 'text-green-600' : 'text-gray-400'}`} title="EV Service" />
  </div>
);

const DualOilIndicator = ({ oilData }) => {
  const oil200L = oilData?.['20058OL'];
  const oil20L = oilData?.['2058OL'];

  const getOilProperties = (data) => {
    if (!data) {
      return {
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300',
        textColor: 'text-gray-400',
        tooltip: 'No purchase data',
        daysSince: '-',
        percentageLeft: null
      };
    }

    const percentageLeft = data.cycleInfo?.percentageLeft;
    const daysSince = data.cycleInfo?.daysSinceLastSale || 0;
    let bgColor, borderColor, textColor, tooltip;

    if (percentageLeft >= 70) {
      bgColor = 'bg-green-100';
      borderColor = 'border-green-500';
      textColor = 'text-green-700';
      tooltip = `${percentageLeft}% of cycle remaining (${daysSince} days since last purchase)`;
    } else if (percentageLeft >= 30) {
      bgColor = 'bg-amber-100';
      borderColor = 'border-amber-500';
      textColor = 'text-amber-700';
      tooltip = `${percentageLeft}% of cycle remaining (${daysSince} days since last purchase)`;
    } else {
      bgColor = 'bg-red-100';
      borderColor = 'border-red-500';
      textColor = 'text-red-700';
      tooltip = `${daysSince} days since last purchase - Ready to call!`;
    }

    return { bgColor, borderColor, textColor, tooltip, daysSince, percentageLeft };
  };

  const oil200Props = getOilProperties(oil200L);
  const oil20Props = getOilProperties(oil20L);

  return (
    <div className="flex flex-col w-24 gap-2">
      <div
        className={`flex items-center justify-between w-full px-2 py-1 rounded ${oil200Props.bgColor} border ${oil200Props.borderColor}`}
        title={oil200Props.tooltip}
      >
        <span className="text-xs font-semibold">200L</span>
        {oil200Props.percentageLeft !== null ? (
          <div className={`text-xs font-bold ${oil200Props.textColor}`}>
            {oil200Props.daysSince}d
          </div>
        ) : (
          <div className="w-3 h-3 rounded-full bg-gray-200"></div>
        )}
      </div>
      <div
        className={`flex items-center justify-between w-full px-2 py-1 rounded ${oil20Props.bgColor} border ${oil20Props.borderColor}`}
        title={oil20Props.tooltip}
      >
        <span className="text-xs font-semibold">20L</span>
        {oil20Props.percentageLeft !== null ? (
          <div className={`text-xs font-bold ${oil20Props.textColor}`}>
            {oil20Props.daysSince}d
          </div>
        ) : (
          <div className="w-3 h-3 rounded-full bg-gray-200"></div>
        )}
      </div>
    </div>
  );
};

const OpeningStatusIndicator = ({ customerId, garageName }) => {
  const { uinfo } = useContext(GlobalContext);
  const [showTooltip, setShowTooltip] = useState(false);
  const [openingHours, setOpeningHours] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOpeningHours = async () => {
      if (!customerId) return;
      setLoading(true);
      try {
        const response = await haxios.post('/visiondashboard/customers/opening-status', {
          customerAccounts: [customerId]
        });
        if (response.data && response.data[customerId]) {
          setOpeningHours(response.data[customerId]);
        }
      } catch (error) {
        console.error('Error fetching opening hours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOpeningHours();
  }, [customerId]);

  if (loading) {
    return (
      <div className="flex justify-center">
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!openingHours) {
    return (
      <div className="flex justify-center" title="No opening hours data">
        <ClockIcon className="w-5 h-5 text-gray-400" />
      </div>
    );
  }

  const now = new Date();
  const currentDay = now.toLocaleDateString('en-GB', { weekday: 'lowercase' });
  const currentTime = now.toTimeString().slice(0, 5);
  const todayHours = openingHours[currentDay];

  const isOpen = todayHours && todayHours.open && todayHours.close &&
    currentTime >= todayHours.open && currentTime <= todayHours.close;

  return (
    <div
      className="relative flex justify-center"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className={`w-4 h-4 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
      {showTooltip && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white border rounded-lg shadow-lg p-3 min-w-48 max-w-xs z-50">
          <div className="font-semibold text-sm mb-2 text-gray-800">
            {garageName || 'Opening Hours'}
          </div>
          <div className="text-xs space-y-1">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
              const dayHours = openingHours[day];
              const isCurrentDay = day === currentDay;
              const displayName = day.charAt(0).toUpperCase() + day.slice(1);
              const formatTime = (time) => {
                if (!time || time.trim() === '') return 'Closed';
                return time;
              };

              return (
                <div
                  key={day}
                  className={`flex justify-between ${isCurrentDay ? 'font-bold text-blue-600' : 'text-gray-600'}`}
                >
                  <span>{displayName}:</span>
                  <span>
                    {dayHours?.open && dayHours?.close
                      ? `${formatTime(dayHours.open)} - ${formatTime(dayHours.close)}`
                      : 'Closed'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

function CustomerSpendsTable() {
  const { uinfo } = useContext(GlobalContext);
  const [activeColorFilters, setActiveColorFilters] = useState([]);
  const [activeRankFilters, setActiveRankFilters] = useState([]);
  const [showZeroYesterdayOnly, setShowZeroYesterdayOnly] = useState(false);
  const [customerStatuses, setCustomerStatuses] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState({});
  const [customerPhoneFields, setCustomerPhoneFields] = useState({});
  const [customerOilData, setCustomerOilData] = useState({});
  const [loadingOilData, setLoadingOilData] = useState({});
  const [customerContactStatus, setCustomerContactStatus] = useState({});
  const [selectedCustomerForComments, setSelectedCustomerForComments] = useState(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);

  const { data: customerSalesData, isLoading: custSalesLoading } = useApi({
    url: `/visiondashboard/rankCustomers?Rep=${uinfo?.Inits}`,
    initial: []
  });

  const { data: salesMarginByBranchAndAccountToday } = useApi({
    url: '/salesassist/cplusassist/salesMarginByBranchAndAccountToday',
    initial: []
  });

  const { data: commentCounts } = useApi({
    url: '/visiondashboard/messages/counts',
    initial: {}
  });

  // Fetch contact status
  useEffect(() => {
    const fetchContactStatus = async () => {
      if (!uinfo?.Inits) return;
      try {
        const response = await haxios.get(
          `/visiondashboard/customer-contact-status?operator_inits=${uinfo.Inits}`
        );
        if (response.success) {
          setCustomerContactStatus(response.data || {});
        }
      } catch (error) {
        console.error('Error fetching contact status:', error);
      }
    };
    fetchContactStatus();
  }, [uinfo]);

  // Fetch customer statuses
  useEffect(() => {
    const fetchCustomerStatuses = async () => {
      if (!customerSalesData || customerSalesData.length === 0) return;
      setCustomerStatuses({});
      setLoadingStatuses({});
      setCustomerPhoneFields({});

      customerSalesData.forEach(async (customer) => {
        const acctNo = customer._id;
        if (!acctNo) return;
        setLoadingStatuses(prev => ({ ...prev, [acctNo]: true }));
        try {
          const response = await haxios.get(`/visiondashboard/salesassist/customer-status/${acctNo}`);
          if (response.success) {
            setCustomerStatuses(prev => ({
              ...prev,
              [acctNo]: response.status
            }));
            setCustomerPhoneFields(prev => ({
              ...prev,
              [acctNo]: {
                field: response.callData.phoneField || 'Tel',
                value: response.callData.phoneNumber
              }
            }));
          }
        } catch (error) {
          console.error(`Failed to fetch status for ${acctNo}:`, error);
        } finally {
          setLoadingStatuses(prev => ({ ...prev, [acctNo]: false }));
        }
      });
    };
    fetchCustomerStatuses();
  }, [customerSalesData]);

  // Fetch oil data
  useEffect(() => {
    const fetchOilData = async () => {
      if (!customerSalesData || customerSalesData.length === 0) return;
      setCustomerOilData({});
      setLoadingOilData({});

      const fetchPromises = customerSalesData.map(async (customer) => {
        const acctNo = customer._id;
        if (!acctNo) return;
        setLoadingOilData(prev => ({ ...prev, [acctNo]: true }));
        try {
          const response = await haxios.get(`/visiondashboard/salesassist/getLastOil/${acctNo}`);
          setCustomerOilData(prev => ({
            ...prev,
            [acctNo]: response.data || response
          }));
        } catch (error) {
          console.error(`Failed to fetch oil data for ${acctNo}:`, error);
        } finally {
          setLoadingOilData(prev => ({ ...prev, [acctNo]: false }));
        }
      });
      await Promise.all(fetchPromises);
    };
    fetchOilData();
  }, [customerSalesData]);

  const toggleContactStatus = async (accountId) => {
    try {
      const newStatus = !customerContactStatus[accountId];
      setCustomerContactStatus(prev => ({
        ...prev,
        [accountId]: newStatus
      }));
      await haxios.post('/visiondashboard/customer-contact-status', {
        account_id: accountId,
        contacted: newStatus,
        operator_inits: uinfo?.Inits
      });
    } catch (error) {
      console.error('Error updating contact status:', error);
      setCustomerContactStatus(prev => ({
        ...prev,
        [accountId]: !customerContactStatus[accountId]
      }));
    }
  };

  const getStatusTooltip = (customerId) => {
    const status = customerStatuses[customerId];
    const phoneInfo = customerPhoneFields[customerId];
    if (!status) return 'Unknown status';
    let tooltip = status.text;
    if (phoneInfo) {
      tooltip += ` (using ${phoneInfo.field}: ${phoneInfo.value})`;
    }
    return tooltip;
  };

  const handleCommentClick = (customer) => {
    setSelectedCustomerForComments(customer);
    setMessageDialogOpen(true);
  };

  const customerSpends = customerSalesData
    ?.map((customer) => {
      const customerData = salesMarginByBranchAndAccountToday.find(el => el.account === customer._id);
      const messageData = commentCounts.data?.find(m => m.Acct === customer._id);
      return {
        ...customer,
        color: customer.totalSalesToday > 0 ? 'green' :
          (customerData?.quotesCount > 0 ? 'orange' : 'red'),
        messageCount: messageData?.messageCount || 0
      };
    })
    .sort((a, b) => b.color.localeCompare(a.color)) || [];

  const customerSpendsFiltered = customerSpends.filter((el) => {
    const colorMatch = activeColorFilters.length === 0 || activeColorFilters.includes(el.color);
    const rankMatch = activeRankFilters.length === 0 || activeRankFilters.includes(el.CustomerRank);
    const zeroYesterdayMatch = !showZeroYesterdayOnly || 
      (el.totalSalesYesterday === 0 || el.totalSalesYesterday === null || 
       el.totalSalesYesterday === undefined || parseFloat(el.totalSalesYesterday) === 0);
    return colorMatch && rankMatch && zeroYesterdayMatch;
  });

  const availableRanks = [...new Set(customerSpends.map(customer => customer.CustomerRank).filter(Boolean))].sort();

  const clearColorFilters = () => setActiveColorFilters([]);
  const clearRankFilters = () => setActiveRankFilters([]);

  if (custSalesLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Loading customer data...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Filters */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div className="flex items-center space-x-6">
          {/* Rank Filters */}
          <div className="flex items-center">
            <div className="mr-2 text-sm font-medium">Filter by rank</div>
            {availableRanks.map((rank) => (
              <div
                key={rank}
                className={clsx(
                  'mr-1 w-6 h-6 rounded-full cursor-pointer transition-all duration-200 flex items-center justify-center text-xs font-bold text-white',
                  rank === 'A' && 'bg-green-500',
                  rank === 'B' && 'bg-blue-500',
                  rank === 'C' && 'bg-orange-500',
                  rank === 'D' && 'bg-yellow-500',
                  rank === 'E' && 'bg-purple-500',
                  rank === 'F' && 'bg-red-500',
                  rank === 'N/A' && 'bg-gray-500',
                  activeRankFilters.includes(rank)
                    ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                    : 'opacity-50 hover:opacity-100'
                )}
                onClick={() => {
                  if (activeRankFilters.includes(rank)) {
                    setActiveRankFilters(activeRankFilters.filter(r => r !== rank));
                  } else {
                    setActiveRankFilters([...activeRankFilters, rank]);
                  }
                }}
              >
                {rank}
              </div>
            ))}
          </div>

          {/* Color Filters */}
          <div className="flex items-center">
            <div className="mr-2 text-sm font-medium">Filter by color</div>
            <div
              className={clsx(
                'mr-2 w-7 h-7 bg-red-500 rounded-full cursor-pointer transition-all duration-200',
                activeColorFilters.includes('red')
                  ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                  : 'opacity-50 hover:opacity-100'
              )}
              onClick={() => {
                if (activeColorFilters.includes('red')) {
                  setActiveColorFilters(activeColorFilters.filter(c => c !== 'red'));
                } else {
                  setActiveColorFilters([...activeColorFilters, 'red']);
                }
              }}
            />
            <div
              className={clsx(
                'mr-2 w-7 h-7 bg-orange-500 rounded-full cursor-pointer transition-all duration-200',
                activeColorFilters.includes('orange')
                  ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                  : 'opacity-50 hover:opacity-100'
              )}
              onClick={() => {
                if (activeColorFilters.includes('orange')) {
                  setActiveColorFilters(activeColorFilters.filter(c => c !== 'orange'));
                } else {
                  setActiveColorFilters([...activeColorFilters, 'orange']);
                }
              }}
            />
            <div
              className={clsx(
                'w-7 h-7 bg-green-500 rounded-full cursor-pointer transition-all duration-200',
                activeColorFilters.includes('green')
                  ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                  : 'opacity-50 hover:opacity-100'
              )}
              onClick={() => {
                if (activeColorFilters.includes('green')) {
                  setActiveColorFilters(activeColorFilters.filter(c => c !== 'green'));
                } else {
                  setActiveColorFilters([...activeColorFilters, 'green']);
                }
              }}
            />
          </div>

          {/* Zero Yesterday Toggle */}
          <div className="flex items-center">
            <div className="mr-2 text-sm font-medium">Zero yesterday</div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showZeroYesterdayOnly}
                onChange={(e) => setShowZeroYesterdayOnly(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-y-auto max-h-96" style={{ overflowX: 'visible' }}>
        <table className="min-w-full">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="text-sm font-bold border-b">
              <th className="px-2 py-1 bg-white"></th>
              <th className="px-2 py-1 text-left bg-white">Acct</th>
              <th className="px-2 py-1 text-left bg-white">Garage</th>
              <th className="px-2 py-1 text-left bg-white">Rank</th>
              <th className="px-2 py-1 text-left bg-white">30d</th>
              <th className="px-2 py-1 text-left bg-white">Today</th>
              <th className="px-2 py-1 text-left bg-white">Yesterday</th>
              <th className="px-2 py-1 text-left bg-white">Services</th>
              <th className="px-2 py-1 text-center bg-white">Call Status</th>
              <th className="px-2 py-1 text-left bg-white">Comments</th>
              <th className="px-2 py-1 text-center bg-white">Oil</th>
              <th className="px-2 py-1 text-center bg-white">Open Now</th>
              <th className="px-2 py-1 text-center bg-white">Contacted</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {customerSpendsFiltered.map((customer, index) => (
              <tr key={index} className="border-b hover:bg-gray-50">
                <td className="px-2 py-1">
                  <div className={clsx(
                    'w-5 h-5 rounded-full',
                    customer.color === 'red' && 'bg-red-500',
                    customer.color === 'orange' && 'bg-orange-500',
                    customer.color === 'green' && 'bg-green-500'
                  )} />
                </td>
                <td className="px-2 py-1">{customer._id}</td>
                <td className="px-2 py-1">{customer.GarageName}</td>
                <td className="px-2 py-1">
                  <span className={clsx(
                    'px-2 py-1 rounded-full text-white text-xs',
                    customer.CustomerRank === 'A' && 'bg-green-500',
                    customer.CustomerRank === 'B' && 'bg-blue-500',
                    customer.CustomerRank === 'C' && 'bg-orange-500',
                    customer.CustomerRank === 'D' && 'bg-yellow-500',
                    customer.CustomerRank === 'E' && 'bg-purple-500',
                    customer.CustomerRank === 'F' && 'bg-red-500',
                    customer.CustomerRank === 'N/A' && 'bg-gray-500'
                  )}>
                    {customer.CustomerRank || 'N/A'}
                  </span>
                </td>
                <td className="px-2 py-1">£{formatNumber(customer.totalSalesLast30Days)}</td>
                <td className="px-2 py-1">£{formatNumber(customer.totalSalesToday)}</td>
                <td className="px-2 py-1">£{formatNumber(customer.totalSalesYesterday)}</td>
                <td className="px-2 py-1">
                  <ServiceIcons
                    mot={customer.serviceFlags?.provides_mot}
                    oil={customer.serviceFlags?.provides_oil_service}
                    ev={customer.serviceFlags?.provides_ev_service}
                  />
                </td>
                <td className="px-2 py-1 text-center">
                  {loadingStatuses[customer._id] ? (
                    <div className="w-4 h-4 mx-auto border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <div
                      title={getStatusTooltip(customer._id)}
                      className="text-lg cursor-help"
                    >
                      {customerStatuses[customer._id]?.emoji || '❓'}
                    </div>
                  )}
                </td>
                <td
                  className="px-2 py-1 cursor-pointer hover:text-blue-500"
                  onClick={() => handleCommentClick({
                    id: customer._id,
                    garageName: customer.GarageName
                  })}
                >
                  {customer.messageCount || 0}
                </td>
                <td className="px-2 py-1 text-center">
                  {loadingOilData[customer._id] ? (
                    <div className="w-4 h-4 mx-auto border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <DualOilIndicator oilData={customerOilData[customer._id]} />
                  )}
                </td>
                <td className="px-2 py-1 text-center">
                  <OpeningStatusIndicator
                    customerId={customer._id}
                    garageName={customer.GarageName}
                  />
                </td>
                <td className="px-2 py-1 text-center">
                  <button
                    className={`w-6 h-6 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-200 ${
                      customerContactStatus[customer._id]
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200'
                    }`}
                    onClick={() => toggleContactStatus(customer._id)}
                    title={customerContactStatus[customer._id] ? "Contacted" : "Not contacted"}
                  >
                    {customerContactStatus[customer._id] && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {customerSpendsFiltered.length === 0 && (
              <tr>
                <td colSpan="13" className="py-8 text-center text-gray-500">
                  No customers found matching filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Message Dialog */}
      {messageDialogOpen && selectedCustomerForComments && (
        <MessageDialog
          accountId={selectedCustomerForComments.id}
          garageName={selectedCustomerForComments.garageName}
          isOpen={messageDialogOpen}
          onClose={() => {
            setMessageDialogOpen(false);
            setSelectedCustomerForComments(null);
          }}
        />
      )}
    </div>
  );
}

export default CustomerSpendsTable;

